<template>
    <vx-table-row>
        <vx-table-row-title>
            <vx-badge color="slate" size="xs" class="font-mono" data-test="campaign-id">
                {{ props.modelValue.campaign_id }}
            </vx-badge>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Source: </template>
            <span data-test="source">{{ sourceLabels[props.modelValue.source] }}</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="brand">
            <template v-slot:label> Brand: </template>
            <kiosk-tcr-brand-cell
                v-if="props.modelValue.brand_id"
                :brandId="props.modelValue.brand_id"
                :displayName="props.modelValue.brand_display_name"
            ></kiosk-tcr-brand-cell>
            <span v-else>—</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Brand Trust: </template>
            <span data-test="brand-trust">{{
                props.modelValue.brand_id
                    ? verificationLabel(props.modelValue.brand_verified, props.modelValue.brand_vetting_score)
                    : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Use Case: </template>
            <span data-test="usecase">{{
                props.modelValue.usecase ? useCaseLabels[props.modelValue.usecase] : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <span data-test="status">{{
                props.modelValue.status ? campaignStatusLabels[props.modelValue.status] : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> AT&T Class: </template>
            <span
                v-if="props.modelValue.att_message_class"
                :title="messageClassDetail(props.modelValue.att_message_class) ?? undefined"
                data-test="message-class"
            >
                {{ props.modelValue.att_message_class }}
            </span>
            <span v-else>—</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="phone-numbers-count">
            <template v-slot:label> Numbers: </template>
            {{ props.modelValue.phone_numbers_count }}
        </vx-table-row-item>

        <vx-table-row-item data-test="synced-at">
            <template v-slot:label> Synced: </template>
            <span :key="syncedAtKey" :title="shortDateTime(props.modelValue.synced_at)">
                {{ fromNow(props.modelValue.synced_at) }}
            </span>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button :href="phoneNumbersHref" size="sm" data-test="view" outline color="muted">
                View <font-awesome-icon widthAuto :icon="faEye" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script lang="ts" setup>
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { computed } from 'vue';
import KioskTcrBrandCell from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrBrandCell.vue';
import {
    campaignStatusLabels,
    messageClassDetail,
    sourceLabels,
    useCaseLabels,
    verificationLabel,
} from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import type { TcrCampaign } from '~/services/adminTenDlcClient.types';
import { fromNow, shortDateTime } from '~/utils/date';
import { stringifyQueryString } from '~/utils/string';

const props = defineProps<{
    modelValue: TcrCampaign;
}>();

const syncedAtKey = useInterval(60000);

const phoneNumbersHref = computed(
    () => `/kiosk/ten-dlc/phone-numbers?${stringifyQueryString({ campaign_id: props.modelValue.campaign_id })}`
);
</script>
